// In this file you'll find functions for transforming `ComposedOccurrence` into
// various formats that are consumed by the Insights and Monitoring pages
import { GroupBy, SortBy, Category } from "./types";
import { groupByRule, groupBySite, groupByPriority } from "./groups";
import { sortByHits, sortByEquips, sortByInsights, sortByAlpha } from "./sorts";

/**
 * Convert the UI state value for grouping into the key from the API response
 */
export function mapEnumToAnalysisGroupKey(groupBy: GroupBy) {
  switch (groupBy) {
    case GroupBy.Rule:
      return "rule";
    case GroupBy.Priority:
      return "priority";
    case GroupBy.Category:
      return "category";
    default:
      return "site";
  }
}

/**
 * Convert the UI state value for the active category into the key from the API
 * response
 */
export function mapSelectedCategoryToCategoryKey(
  category: Category
): keyof Insite.API.Insights["category"] {
  switch (category) {
    case Category.Metering:
      return "metering";
    case Category.Performance:
      return "performance";
    case Category.Connectivity:
      return "connectivity";
    case Category.Comfort:
      return "comfort";
    default:
      return "null";
  }
}

/**
 * Using `composeInsights` and `lodash.groupBy`, group "grouped" occurrences
 * into composed insights ready for rendering.
 */
export function groupOccurrencesIntoInsights(
  analysisGroup: Insite.Insights.ComposedOccurrence[][],
  groupByValue: GroupBy
): Insite.Insights.ComposedInsight[][] {
  const insights = analysisGroup.map((group) => {
    switch (groupByValue) {
      case GroupBy.Rule:
        return groupByRule(group);
      case GroupBy.Priority:
        return groupByPriority(group);
      default:
        return groupBySite(group);
    }
  });

  return insights;
}

/**
 * Sort composed insights by a supported sort method
 *
 * @param data Pre-grouped composed insights
 * @param groupBy the current insights group
 * @param sortBy the sort option in use
 */
export function sortInsights(
  data: Insite.Insights.ComposedInsight[][],
  groupBy: GroupBy,
  sortBy: SortBy
): Insite.Insights.ComposedInsight[][] {
  if (data.length === 0) return [];

  switch (sortBy) {
    case SortBy.Equips:
      return sortByEquips(data);
    case SortBy.InsightsAsc:
      return sortByInsights(data, "asc");
    case SortBy.InsightsDesc:
      return sortByInsights(data, "desc");
    case SortBy.AlphaAsc:
      return sortByAlpha(data, groupBy, "asc");
    case SortBy.AlphaDesc:
      return sortByAlpha(data, groupBy, "desc");
    case SortBy.Hits:
    default:
      return sortByHits(data);
  }
}
